<template>
  <div class="view-upload" :class="{ 'view-upload--error': showError }">
    <div class="view-header">
      <p class="view-label">{{ maskName }}</p>
      <v-chip x-small :color="isMandatory ? 'red' : 'grey'" dark class="view-badge">
        {{ isMandatory ? $t('addShooting.mandatory') : $t('addShooting.optionalLabel') }}
      </v-chip>
    </div>
    <div
      class="drop-zone"
      :class="{ 'has-image': hasFile, 'drop-zone--error': showError }"
      @drop.prevent="onDrop"
      @dragover.prevent
    >
      <template v-if="!hasFile">
        <img v-if="maskUrl" :src="maskUrl" class="mask-overlay" />
        <v-icon size="36" class="mb-4 icon-add-photos">add_to_photos</v-icon>
        <p>{{ $t('addShooting.dragAndDroplabel') }}</p>
        <v-btn color="primary" small @click="openFileDialog">{{ $t('addShooting.dragAndDropButton') }}</v-btn>
      </template>
      <template v-else>
        <div class="preview-container">
          <img :src="previewUrl" class="preview-image" />
          <v-btn icon small class="remove-btn" @click="removeFile">
            <v-icon color="white">close</v-icon>
          </v-btn>
        </div>
      </template>
    </div>
    <p v-if="showError" class="error-text red--text text-caption mt-1">
      {{ $t('addShooting.mandatoryViewRequired') }}
    </p>
    <input :ref="'fileInput-' + maskOrder" type="file" accept="image/*" class="hidden-input" @change="onFileChange" />
  </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'ViewImageUpload',

  props: {
    mask: {
      type: Object,
      required: true
    },
    showValidation: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      previewUrl: null
    }
  },

  computed: {
    ...mapState({
      viewImages: (state) => state.newShooting.viewImages
    }),
    maskName() {
      return this.mask.name
    },
    maskOrder() {
      return this.mask.order
    },
    maskUrl() {
      return this.mask.maskUrl || null
    },
    file() {
      return this.viewImages[this.maskOrder] || null
    },
    hasFile() {
      return !!this.file
    },
    isMandatory() {
      return !this.mask.options?.optional
    },
    showError() {
      return this.showValidation && this.isMandatory && !this.hasFile
    }
  },

  watch: {
    file: {
      immediate: true,
      handler(newFile) {
        if (this.previewUrl) {
          URL.revokeObjectURL(this.previewUrl)
        }
        this.previewUrl = newFile ? URL.createObjectURL(newFile) : null
      }
    }
  },

  // eslint-disable-next-line vue/no-deprecated-destroyed-lifecycle
  beforeDestroy() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl)
    }
  },

  methods: {
    openFileDialog() {
      this.$refs['fileInput-' + this.maskOrder].click()
    },
    onFileChange(e) {
      const file = e.target.files && e.target.files[0]
      if (file && file.type.startsWith('image/')) {
        this.$store.commit('UPDATE_VIEW_IMAGE', { maskOrder: this.maskOrder, file })
      }
      e.target.value = ''
    },
    onDrop(e) {
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]
      if (file && file.type.startsWith('image/')) {
        this.$store.commit('UPDATE_VIEW_IMAGE', { maskOrder: this.maskOrder, file })
      }
    },
    removeFile() {
      this.$store.commit('REMOVE_VIEW_IMAGE', this.maskOrder)
    }
  }
}
</script>

<style lang="less" scoped>
.view-upload {
  margin-bottom: 16px;
}

.view-upload--error {
  .drop-zone {
    border-color: #ff5252;
  }
}

.view-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.view-label {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.7);
  margin-bottom: 0;
}

.view-badge {
  font-size: 10px;
}

.drop-zone {
  border: 1px dashed lightgray;
  height: 200px;
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  position: relative;

  &.has-image {
    border-style: solid;
  }

  &--error {
    border-color: #ff5252;
  }
}

.mask-overlay {
  position: absolute;
  max-width: 80%;
  max-height: 80%;
  object-fit: contain;
  opacity: 0.15;
  pointer-events: none;
  filter: invert(1);
}

.preview-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  padding: 5px;
  border-radius: 10px;
}

.remove-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  background-color: rgba(0, 0, 0, 0.5);
}

.hidden-input {
  display: none;
}

.error-text {
  margin: 0;
}

p {
  text-align: center;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.7);
}
</style>
